// Clé utilisée pour stocker les cartes dans le localStorage
const STORAGE_KEY = 'business_cards';
const DEFAULT_COMPANY_LOGO_KEY = 'default_company_logo';

/**
 * Récupère toutes les cartes depuis le localStorage
 * @returns {Array} Tableau des cartes de visite
 */
export const getCards = () => {
  const cardsJSON = localStorage.getItem(STORAGE_KEY);
  return cardsJSON ? JSON.parse(cardsJSON) : [];
};

/**
 * Récupère une carte spécifique par son identifiant
 * @param {string} id - Identifiant de la carte à récupérer
 * @returns {Object|null} - La carte si trouvée, null sinon
 */
export const getCardById = (id) => {
  const cards = getCards();
  return cards.find(c => c.id === id) || null;
};

/**
 * Sauvegarde une carte (nouvelle ou mise à jour)
 * @param {Object} card - Données de la carte à sauvegarder
 */
export const saveCard = (card) => {
  const cards = getCards();
  const defaultLogo = getDefaultCompanyLogo();
  
  // Si le logo de l'entreprise n'est pas défini et que l'entreprise est définie,
  // utiliser le logo par défaut s'il existe
  if (!card.companyLogo && card.company && defaultLogo && defaultLogo.data) {
    card.companyLogo = defaultLogo.data;
    card.companyLogoSize = defaultLogo.size || 'medium';
  }
  
  // Vérifier si la carte existe déjà
  const existingCardIndex = cards.findIndex(c => c.id === card.id);
  
  if (existingCardIndex >= 0) {
    // Mise à jour d'une carte existante
    cards[existingCardIndex] = card;
  } else {
    // Ajout d'une nouvelle carte
    cards.push(card);
  }
  
  // Sauvegarde dans le localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cards));
  return card;
};

/**
 * Supprime une carte par son identifiant
 * @param {string} id - Identifiant de la carte à supprimer
 * @returns {boolean} - True si supprimé avec succès
 */
export const deleteCard = (id) => {
  const cards = getCards();
  const filteredCards = cards.filter(c => c.id !== id);
  
  // Si le nombre de cartes a changé, une carte a été supprimée
  if (filteredCards.length !== cards.length) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredCards));
    return true;
  }
  
  return false;
};

/**
 * Définit un logo d'entreprise par défaut pour toutes les cartes
 * @param {string} logoDataJSON - Données du logo (objet JSON stringifié avec data et size)
 */
export const setDefaultCompanyLogo = (logoDataJSON) => {
  localStorage.setItem(DEFAULT_COMPANY_LOGO_KEY, logoDataJSON);
};

/**
 * Récupère le logo d'entreprise par défaut
 * @returns {Object|null} - Objet avec les données du logo et sa taille, ou null si non défini
 */
export const getDefaultCompanyLogo = () => {
  const logoData = localStorage.getItem(DEFAULT_COMPANY_LOGO_KEY);
  if (!logoData) return null;
  
  try {
    // Si c'est un objet JSON stringifié (nouveau format)
    return JSON.parse(logoData);
  } catch (e) {
    // Si c'est juste une URL ou une chaîne base64 (ancien format)
    return { data: logoData, size: 'medium' };
  }
};

/**
 * Applique le logo par défaut à toutes les cartes existantes qui ont une entreprise mais pas de logo
 */
export const applyDefaultLogoToAll = () => {
  const defaultLogo = getDefaultCompanyLogo();
  if (!defaultLogo || !defaultLogo.data) return false;
  
  const cards = getCards();
  let updated = false;
  
  cards.forEach(card => {
    if (card.company && !card.companyLogo) {
      // Stocker les informations complètes du logo dans chaque carte
      card.companyLogo = defaultLogo.data;
      card.companyLogoSize = defaultLogo.size || 'medium';
      updated = true;
    }
  });
  
  if (updated) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cards));
  }
  
  return updated;
};
